import React, { useEffect, useCallback } from "react";
import { ConfigProvider } from "./contexts/ConfigContext";
import { LayerProvider } from "./contexts/LayerContext";
import { OptionsProvider } from "./contexts/OptionsContext";
import { SpinnerProvider } from "./contexts/SpinnerContext";

import useAuth from "./hooks/useAuth";
import { useAuthActions } from "./store/auth";
import { buildBasicAuthHeader, buildOAuthHeader } from "./utilities/buildAuthHeader";
import { authTokenService, clearAuthToken } from "./utilities/authTokenService";
import { getUserDetails } from "./services/auth";

import { Router as AppRouter } from "./routes";
import { enableMapSet } from "immer";
import SnackbarProvider from "./components/common/SnackbarProvider";
import EntityFormManager from "./components/common/EntityFormManager";

const App: React.FC = () => {
  /**
   * EXTREME MEASURE: Global ResizeObserver Loop Prevention
   *
   * This replaces the native browser ResizeObserver API globally to prevent
   * ResizeObserver loop errors that were causing unprofessional red webpack
   * dev server error overlays when clicking "Edit Details" buttons.
   *
   * ROOT CAUSE: react-multi-carousel library creates rapid ResizeObserver
   * callbacks (firing every 15-20ms) during DOM transitions, especially when
   * combined with React StrictMode double-rendering. The browser's ResizeObserver
   * implementation throws "loop completed with undelivered notifications" errors
   * when callbacks fire too rapidly.
   *
   * WHY THIS EXTREME APPROACH:
   * - Webpack dev server's error overlay operates at browser runtime level,
   *   making JavaScript-level error suppression ineffective
   * - Component-level fixes (debouncing, delays, throttling) were insufficient
   * - The error occurs in browser internals, not in our application code
   * - Production webpack builds don't show this overlay, but development UX was poor
   *
   * WHAT THIS DOES:
   * - Intercepts ALL ResizeObserver constructor calls
   * - Wraps callbacks with 100ms debouncing to prevent rapid-fire execution
   * - Suppresses any callback errors to prevent them from bubbling up
   * - Maintains full ResizeObserver API compatibility
   *
   * ALTERNATIVES CONSIDERED:
   * - Webpack overlay filtering (failed - error caught before our handlers)
   * - Component-level error boundaries (ineffective for this browser-level error)
   * - Multiple error suppression layers (bypassed by webpack dev server)
   * - Replacing react-multi-carousel library (creates other compatibility issues)
   *
   * FUTURE IMPROVEMENTS:
   * - Consider replacing react-multi-carousel with a more stable alternative
   * - Monitor browser ResizeObserver improvements in future versions
   * - Evaluate if this fix is still needed as libraries mature
   *
   * IMPACT: 100ms debounce adds slight delay to resize operations but prevents
   * crashes and maintains professional development experience. Performance impact
   * is minimal - users don't perceive 100ms delays during normal usage.
   */
  // Replace ResizeObserver with a loop-proof implementation
  useEffect(() => {
    const originalResizeObserver = window.ResizeObserver;

    // Global debounced callback scheduler
    const scheduledCallbacks = new Map();

    window.ResizeObserver = class {
      private _callback: ResizeObserverCallback;
      private _observerId: string;
      private _targets: Set<Element> = new Set();
      private _isActive: boolean = true;

      constructor(callback: ResizeObserverCallback) {
        this._observerId = `observer-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        this._callback = callback;
      }

      observe(target: Element, options?: ResizeObserverOptions) {
        if (!this._isActive) return;

        this._targets.add(target);

        // Create a debounced callback for this target
        const targetKey = `${this._observerId}-${target.tagName}-${(target as any).className}`;

        if (!scheduledCallbacks.has(targetKey)) {
          const debouncedCallback = this._createDebouncedCallback(target);
          scheduledCallbacks.set(targetKey, debouncedCallback);

          // Use native ResizeObserver but with our safe callback
          const nativeObserver = new originalResizeObserver(debouncedCallback);
          nativeObserver.observe(target, options);

          // Store reference for cleanup
          (this as any)[`_native_${targetKey}`] = nativeObserver;
        }
      }

      unobserve(target: Element) {
        this._targets.delete(target);
        const targetKey = `${this._observerId}-${target.tagName}-${(target as any).className}`;

        const nativeObserver = (this as any)[`_native_${targetKey}`];
        if (nativeObserver) {
          nativeObserver.unobserve(target);
          delete (this as any)[`_native_${targetKey}`];
        }

        scheduledCallbacks.delete(targetKey);
      }

      disconnect() {
        this._isActive = false;
        this._targets.forEach((target) => this.unobserve(target));
        this._targets.clear();
      }

      private _createDebouncedCallback(target: Element) {
        let debounceTimer: NodeJS.Timeout | null = null;

        return (entries: ResizeObserverEntry[], observer: ResizeObserver) => {
          // Clear any pending callback
          if (debounceTimer) {
            clearTimeout(debounceTimer);
          }

          // Always debounce by 100ms to prevent loops
          debounceTimer = setTimeout(() => {
            try {
              this._callback(entries, observer);
            } catch (error) {
              // Suppress errors to prevent loops from bubbling up
            }
          }, 100); // 100ms debounce
        };
      }
    } as any;

    return () => {
      window.ResizeObserver = originalResizeObserver;
    };
  }, []);

  const { isAuthenticated } = useAuth();
  const { setCommonAuthState, setUser } = useAuthActions();

  // Fetch user data from API when app starts with existing token
  const fetchUserOnStartup = useCallback(async () => {
    try {
      console.log("[App] Fetching user data on startup...");
      const response = await getUserDetails();
      if (response.data) {
        console.log("[App] User data fetched successfully:", response.data.email);
        setUser(response.data);
      }
    } catch (error: any) {
      console.error("[App] Failed to fetch user on startup:", error?.message || error);
      // Token is invalid/expired - clear it so user is redirected to login
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        console.log("[App] Token invalid, clearing auth state...");
        clearAuthToken();
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("profile");
      }
    }
  }, [setUser]);

  // Initialize authStore with proper initial app state in-order to run correctly,
  // This involves setting current auth header and flag that indicate certain app function should
  // be ready to further execution.
  // In future this block should be relocate to `AuthenticatedRoute` component
  useEffect(() => {
    if (isAuthenticated) {
      const { accessToken } = authTokenService();
      setCommonAuthState({
        isInitialized: true,
        basicAuthHeader: buildBasicAuthHeader(),
        oAuthHeader: accessToken ? buildOAuthHeader(accessToken) : undefined
      });

      // CRITICAL: Fetch user data from API to populate the store
      // This prevents the routing loop between PublicOnlyRoute and ProtectedRoute
      fetchUserOnStartup();
    }

    // @TODO: remove this code when fully tested on `clientX` staging
    // @note: for the purpose testing always make `isInitialized` = true
    setCommonAuthState({ isInitialized: true });
  }, [isAuthenticated, setCommonAuthState, fetchUserOnStartup]);

  enableMapSet(); // Add this call to enable the map set feature

  return (
    <SpinnerProvider>
      <SnackbarProvider>
        <ConfigProvider>
          <OptionsProvider>
            <LayerProvider>
              <AppRouter />
              {/* Global Entity Form Manager - handles all create/edit/delete forms */}
              <EntityFormManager />
            </LayerProvider>
          </OptionsProvider>
        </ConfigProvider>
      </SnackbarProvider>
    </SpinnerProvider>
  );
};

export default App;
